import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { Link as RouterLink } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import MarketingInfoBar from "./MarketingInfoBar";
import PageMeta from "./PageMeta";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "product", label: "Product" },
  { key: "deployment", label: "Deployment" },
  { key: "security", label: "Security" },
  { key: "pricing", label: "Pricing & Onboarding" },
];

const FAQ_ITEMS = [
  {
    category: "product",
    question: "What is Infrablaze?",
    answer:
      "Infrablaze is a visual cloud operations platform for platform and cloud teams. It combines multi-cloud discovery, Auto-IaC generation, policy controls, and cost intelligence so teams can move faster with stronger governance.",
  },
  {
    category: "product",
    question: "How is Infrablaze different from a traditional CSPM or IaC scanner?",
    answer:
      "Most tools only detect issues. Infrablaze also helps you take action by generating production-ready IaC, guiding remediations through pull requests, and enforcing policy gates before and after deployment.",
  },
  {
    category: "product",
    question: "Which cloud providers does Infrablaze support?",
    answer:
      "Infrablaze supports AWS, Azure, and GCP, with workflows designed for multi-account and multi-environment operations.",
  },
  {
    category: "product",
    question: "How does asset discovery and inventory work?",
    answer:
      "Infrablaze connects to your cloud accounts using least-privilege access, inventories resources continuously, and maps dependencies so teams understand what a change touches and who owns it before committing.",
  },
  {
    category: "product",
    question: "Can Infrablaze generate IaC for existing unmanaged resources?",
    answer:
      "Yes. Infrablaze can codify existing resources into structured IaC and route changes through reviewable PR workflows, helping teams reduce unmanaged drift over time.",
  },
  {
    category: "product",
    question: "Does Infrablaze help with cost optimization?",
    answer:
      "Yes. Infrablaze shows cost impact before changes are deployed and helps identify idle or misconfigured resources so teams can reclaim waste with safer controls.",
  },
  {
    category: "deployment",
    question: "Can Infrablaze run in our own environment?",
    answer:
      "Yes. Infrablaze offers both managed SaaS and self-hosted Enterprise deployment options so teams can meet data residency, network, and compliance requirements.",
  },
  {
    category: "deployment",
    question: "How long does onboarding usually take?",
    answer:
      "Most teams can connect initial cloud accounts quickly, then phase in Auto-IaC, policy guardrails, and cost controls incrementally. Full rollout timing depends on account count and governance depth.",
  },
  {
    category: "security",
    question: "How does policy enforcement work?",
    answer:
      "Infrablaze supports policy-as-code workflows and applies checks during pull requests and CI/CD pipelines. Teams can block non-compliant changes, capture evidence, and maintain an audit trail for governance.",
  },
  {
    category: "security",
    question: "Is our data used to train public AI models?",
    answer:
      "No. Infrablaze is designed for enterprise controls and offers deployment options that keep infrastructure data in approved environments. Enterprise customers can also use bring-your-own-LLM patterns.",
  },
  {
    category: "security",
    question: "Does Infrablaze support SSO, RBAC, and audit logs?",
    answer:
      "Yes. Enterprise capabilities include SSO, role-based access controls, and auditability features so teams can enforce least privilege and satisfy internal control requirements.",
  },
  {
    category: "pricing",
    question: "How do we get a demo or start a trial?",
    answer:
      "You can request a walkthrough from our contact page or start with the available plan options in pricing. We help teams choose a rollout path based on cloud footprint and governance goals.",
  },
];

export default function FAQPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [mounted, setMounted] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      setReducedMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    }
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const fadeUp = (delay = 0) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQ_ITEMS.filter((item) => {
      const inCategory = category === "all" || item.category === category;
      const inSearch =
        !q ||
        item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q);
      return inCategory && inSearch;
    });
  }, [category, query]);

  return (
    <Box
      component="main"
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        "@keyframes ibFloatPulse": {
          "0%, 100%": { transform: "translate(-50%, -50%) scale(1)", opacity: 0.55 },
          "50%": { transform: "translate(-50%, -52%) scale(1.06)", opacity: 0.8 },
        },
      }}
    >
      <PageMeta
        title="FAQs — Control Plane for Agent Driven Infrastructure"
        description="Frequently asked questions about InfraBlaze — pricing, deployment, security, multi-cloud support, codification, and BlazeAI."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_ITEMS.map((item) => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer,
            },
          })),
        }}
      />
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          pt: {
            xs: "calc(56px + env(safe-area-inset-top) + 56px)",
            md: "calc(56px + env(safe-area-inset-top) + 88px)",
          },
          pb: { xs: 6, md: 8 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            width: { xs: 500, md: 900 },
            height: { xs: 500, md: 900 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1)}, transparent 60%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
            zIndex: 0,
            animation: reducedMotion ? "none" : "ibFloatPulse 12s ease-in-out infinite",
          }}
        />
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Typography
              variant="h1"
              sx={{
                ...fadeUp(0),
                fontWeight: 950,
                letterSpacing: -1.0,
                lineHeight: 1.04,
                fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.05rem" },
              }}
            >
              How can we help?
            </Typography>
            <Typography
              variant="h6"
              color="text.primary"
              sx={{ ...fadeUp(260), maxWidth: 620, lineHeight: 1.7, fontWeight: 700, fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.4rem" } }}
            >
              Search answers to common questions about InfraBlaze — or reach out
              if you can't find what you're looking for.
            </Typography>

            {/* Search */}
            <Box sx={{ ...fadeUp(400), width: "100%", maxWidth: 560, mt: 1 }}>
              <TextField
                fullWidth
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for answers..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 999,
                    backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.55 : 0.95),
                    backdropFilter: "blur(12px)",
                    fontSize: "1rem",
                    py: 0.5,
                    "& fieldset": {
                      borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.22),
                      borderWidth: 1.5,
                    },
                    "&:hover fieldset": {
                      borderColor: `${alpha(theme.palette.primary.main, 0.55)} !important`,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: `${theme.palette.primary.main} !important`,
                      borderWidth: 2,
                    },
                  },
                }}
              />
            </Box>

            {/* Category chips */}
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent="center"
              sx={{
                ...fadeUp(520),
                rowGap: 1,
                mt: 1,
                maxWidth: 720,
              }}
            >
              {CATEGORIES.map((c) => (
                <Chip
                  key={c.key}
                  label={c.label}
                  onClick={() => setCategory(c.key)}
                  sx={{
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    px: 0.5,
                    height: 34,
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor:
                      category === c.key
                        ? alpha(theme.palette.primary.main, 0.6)
                        : alpha(theme.palette.divider, 0.8),
                    backgroundColor:
                      category === c.key
                        ? alpha(theme.palette.primary.main, isDark ? 0.22 : 0.14)
                        : alpha(theme.palette.background.paper, isDark ? 0.5 : 0.9),
                    color: category === c.key ? theme.palette.primary.main : "text.primary",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12),
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Questions */}
      <Box sx={{ pb: { xs: 7, md: 10 } }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {filtered.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                borderRadius: "16px",
                border: "1px dashed",
                borderColor: alpha(theme.palette.divider, 0.7),
                backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.4 : 0.85),
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                No matches found
              </Typography>
              <Typography color="text.secondary">
                Try a different search term or browse all questions.
              </Typography>
              <Button
                onClick={() => {
                  setQuery("");
                  setCategory("all");
                }}
                sx={{ mt: 2, fontWeight: 800, textTransform: "none" }}
              >
                Clear filters
              </Button>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {filtered.map((item, idx) => (
                <Accordion
                  key={item.question}
                  disableGutters
                  elevation={0}
                  sx={{
                    borderRadius: "14px !important",
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.85),
                    backgroundColor: isDark
                      ? alpha(theme.palette.background.paper, 0.55)
                      : alpha(theme.palette.background.paper, 0.96),
                    backdropFilter: "blur(10px)",
                    transition: "border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease",
                    "&:before": { display: "none" },
                    "&:hover": {
                      borderColor: alpha(theme.palette.primary.main, 0.45),
                      boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, isDark ? 0.14 : 0.08)}`,
                    },
                    "&.Mui-expanded": {
                      borderColor: alpha(theme.palette.primary.main, 0.55),
                      boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1)}`,
                    },
                    opacity: mounted ? 1 : 0,
                    transform: mounted ? "translateY(0)" : "translateY(16px)",
                    transitionProperty: "opacity, transform, border-color, box-shadow",
                    transitionDuration: "0.6s",
                    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
                    transitionDelay: `${Math.min(idx * 50, 400)}ms`,
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{ color: theme.palette.primary.main, fontSize: 24 }}
                      />
                    }
                    sx={{
                      px: { xs: 2.5, md: 3 },
                      py: 0.5,
                      minHeight: 64,
                      "& .MuiAccordionSummary-content": { my: 1.5 },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", pr: 2 }}>
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: { xs: 2.5, md: 3 }, pb: 2.5, pt: 0 }}>
                    <Typography
                      color="text.secondary"
                      sx={{ lineHeight: 1.8, fontSize: "0.94rem" }}
                    >
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </Container>
      </Box>

      {/* Still have questions CTA */}
      <Box sx={{ pb: { xs: 8, md: 11 } }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              position: "relative",
              borderRadius: "20px",
              overflow: "hidden",
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, isDark ? 0.4 : 0.28),
              backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.55 : 0.96),
              backdropFilter: "blur(14px)",
              p: { xs: 4, md: 5 },
              textAlign: "center",
              boxShadow: `0 24px 80px ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12)}`,
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: `radial-gradient(500px 200px at 50% 0%, ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.15)}, transparent 60%)`,
                pointerEvents: "none",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  mb: 2,
                }}
              >
                <ChatBubbleOutlineIcon sx={{ color: "primary.main", fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: -0.5, mb: 1 }}>
                Still have questions?
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 480, mx: "auto", lineHeight: 1.7 }}
              >
                Our team can walk through your environment and recommend a practical
                onboarding path.
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="center"
              >
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    fontWeight: 900,
                    borderRadius: 999,
                    px: 3.5,
                    textTransform: "none",
                    color: "common.white !important",
                    boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.35)}`,
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    "&:hover": {
                      color: "common.white !important",
                      transform: "translateY(-2px)",
                      boxShadow: `0 14px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
                    },
                  }}
                >
                  Contact our team
                </Button>
                <Button
                  component={RouterLink}
                  to="/product"
                  variant="outlined"
                  size="large"
                  sx={{
                    fontWeight: 900,
                    borderRadius: 999,
                    px: 3.5,
                    textTransform: "none",
                    borderWidth: 1.5,
                    "&:hover": { borderWidth: 1.5 },
                  }}
                >
                  Explore product
                </Button>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={{ mt: "auto" }}>
        <MarketingInfoBar />
      </Box>
    </Box>
  );
}
